import type { AirportConfig, Millis, Stand, StandAssignment, StandClosure, Turn } from "./types";
import {
  COST,
  closureHit,
  contactOrder,
  customsGates,
  customsWalkClash,
  dependencyApplies,
  gapMin,
  handlerOf,
  isJet,
  keptTogether,
  needsCustoms,
  staticBlock,
  staticCost,
  wantsBridge,
  type CostPart,
} from "./rules";
import { MIN, formatLocal, overlaps } from "./time";
import { mulberry32 } from "./rng";

export interface AllocateOptions {
  turns: Turn[];
  config: AirportConfig;
  closures?: StandClosure[];
  /** The plan in force today. Locked entries never move; others may, at `changePenalty` each. */
  current?: Record<string, StandAssignment>;
  /** Cost of moving a turn away from its current stand. 0 = plan from scratch. */
  changePenalty?: number;
  /** Multiplier on soft service costs (preferences, remote, fallback gate). */
  serviceWeight?: number;
  /** Aircraft already on block before this instant cannot be moved. */
  now?: Millis;
  seed?: number;
  iterations?: number;
  timeBudgetMs?: number;
}

/** How many more annealing passes a day with aircraft still off a stand may get. */
const EXTRA_PASSES = 6;

/**
 * The most passengers meeting on the customs walk may cost one aircraft, however many it
 * meets. Summed without a ceiling, two or three meetings outweighed having no stand at all,
 * and a transborder jet was left off every gate and then dropped wherever was left - which
 * is how jets ended up on gates without a bridge while a Q400 held the one they needed.
 */
const CUSTOMS_WALK_CEILING = COST.UNALLOCATED * 0.6;

/**
 * The most the tie-breaking rules between aircraft may cost one aircraft, each. An aircraft
 * parked all day shares the ground with every other aircraft of its airline and every jet;
 * summed without a ceiling, keeping it near them all cost more than a stand is worth, and it
 * was left with none.
 */
const SOFT_PAIR_CEILING = COST.UNALLOCATED * 0.1;

/** A visit longer than this is an aircraft parked, not one being turned: no crew to keep together. */
const GROUP_MAX_STAY = 4 * 60 * MIN;

/** The most keeping an airline together may cost one aircraft. */
const TOGETHER_CEILING = 60;
/** The most keeping a ground handler's aircraft close may cost one: a very low priority. */
const HANDLER_CEILING = 15;

/** "WS181/WS182", for the reasons written into an explanation. */
const named = (t: Turn) => [t.arr?.flightNumber, t.dep?.flightNumber].filter(Boolean).join("/") || t.id;

export interface AllocateResult {
  assignments: Record<string, StandAssignment>;
  cost: number;
  iterations: number;
  elapsedMs: number;
}

/**
 * Incremental cost model for stand allocation. Holds the current solution and can
 * price a single move in O(turns on the affected stands).
 */
export class StandModel {
  readonly standById = new Map<string, Stand>();
  readonly turnById = new Map<string, Turn>();
  readonly standOf = new Map<string, string | null>();
  readonly onStand = new Map<string, Set<string>>();
  readonly candidates = new Map<string, string[]>();
  readonly fixed = new Set<string>();
  private readonly unitCache = new Map<string, number>();
  private readonly intlSoon = new Set<string>();
  private readonly depStands = new Map<string, string[]>();
  /** The customs gates, and for each visit the others whose passengers its own would meet on the walk. */
  private readonly customs: Set<string>;
  private readonly walkPartners = new Map<string, string[]>();
  /** Jets and turboprops on the ground at the same time: who would rather have whose bridge. */
  private readonly bridgeRivals = new Map<string, string[]>();
  private readonly jets = new Set<string>();
  /** Aircraft of one airline (Air Canada, WestJet) on the ground together, to be kept close. */
  private readonly groupPartners = new Map<string, string[]>();
  /** Aircraft of one ground handler on the ground together, to be kept close. */
  private readonly handlerPartners = new Map<string, string[]>();
  private readonly order: Map<string, number>;

  constructor(
    readonly turns: Turn[],
    readonly config: AirportConfig,
    readonly closures: StandClosure[],
    readonly baseline: Record<string, StandAssignment>,
    readonly changePenalty: number,
    readonly serviceWeight: number,
  ) {
    for (const s of config.stands) {
      this.standById.set(s.id, s);
      this.onStand.set(s.id, new Set());
    }
    for (const d of config.dependencies) {
      this.depStands.set(d.stand, [...(this.depStands.get(d.stand) ?? []), d.blocks]);
      this.depStands.set(d.blocks, [...(this.depStands.get(d.blocks) ?? []), d.stand]);
    }
    const lookahead = config.settings.intlLookaheadMin * MIN;
    const intlTurns = turns.filter((t) => needsCustoms(t));
    for (const t of turns) {
      this.turnById.set(t.id, t);
      this.standOf.set(t.id, null);
      // A stand set aside for manual use is never chosen automatically, but a
      // planner can still drop an aircraft on it, and one already there stays.
      this.candidates.set(
        t.id,
        config.stands.filter((s) => s.autoAllocate !== false && !staticBlock(t, s, config)).map((s) => s.id),
      );
      if (!needsCustoms(t) && intlTurns.some((i) => overlaps(t.start, t.end + lookahead, i.start, i.end))) {
        this.intlSoon.add(t.id);
      }
    }
    // Whose passengers would meet on the customs walk, worked out once: only an international
    // or transborder arrival starts it, so the pairs are few.
    this.customs = customsGates(config);
    if ((config.settings.customsWalkPenalty ?? 0) > 0 && this.customs.size > 1) {
      for (const i of intlTurns) {
        for (const u of turns) {
          if (u.id === i.id || !customsWalkClash(i, u, config)) continue;
          this.walkPartners.set(i.id, [...(this.walkPartners.get(i.id) ?? []), u.id]);
          this.walkPartners.set(u.id, [...(this.walkPartners.get(u.id) ?? []), i.id]);
        }
      }
    }
    // Bridges: jets first. Every jet against every turboprop it shares time on the ground with.
    const s = config.settings;
    // Jets that want a bridge: a long stay by day or an international night does not.
    for (const t of turns) if (wantsBridge(t, config)) this.jets.add(t.id);
    const link = (m: Map<string, string[]>, a: string, b: string) => {
      m.set(a, [...(m.get(a) ?? []), b]);
      m.set(b, [...(m.get(b) ?? []), a]);
    };
    if ((s.bridgePriorityPenalty ?? 0) > 0) {
      const props = turns.filter((t) => t.aircraftType && !isJet(t.aircraftType, config));
      for (const j of turns) {
        if (!this.jets.has(j.id)) continue;
        for (const p of props) if (overlaps(j.start, j.end, p.start, p.end)) link(this.bridgeRivals, j.id, p.id);
      }
    }
    this.order = contactOrder(config);
    // Only aircraft being worked: one parked for hours, overnight or in storage, has no crew
    // on it, and keeping a whole airline near it made clearing a stand for it cost more
    // than leaving it with none.
    const worked = turns.filter((t) => t.end - t.start <= GROUP_MAX_STAY);
    const pairUp = (m: Map<string, string[]>, keyOf: (t: Turn) => string | null) => {
      const groups = new Map<string, Turn[]>();
      for (const t of worked) {
        const k = keyOf(t);
        if (k) groups.set(k, [...(groups.get(k) ?? []), t]);
      }
      for (const list of groups.values()) {
        for (let i = 0; i < list.length; i++) {
          for (let j = i + 1; j < list.length; j++) {
            if (overlaps(list[i]!.start, list[i]!.end, list[j]!.start, list[j]!.end)) link(m, list[i]!.id, list[j]!.id);
          }
        }
      }
    };
    if ((s.handlerSpreadPenalty ?? 0) > 0) pairUp(this.groupPartners, (t) => (keptTogether(t.airline, config) ? t.airline : null));
    if ((s.handlerTogetherPenalty ?? 3) > 0 && config.groundHandlers?.length) pairUp(this.handlerPartners, (t) => handlerOf(t.airline, config)?.id ?? null);

  }

  /** Cost of a turn sitting on a stand, ignoring other turns. */
  unit(turnId: string, standId: string | null): number {
    if (standId === null) return COST.UNALLOCATED;
    const key = `${turnId}|${standId}`;
    const hit = this.unitCache.get(key);
    if (hit !== undefined) return hit;
    const t = this.turnById.get(turnId)!;
    const s = this.standById.get(standId);
    let c = 0;
    if (!s) c += COST.HARD;
    else {
      if (staticBlock(t, s, this.config)) c += COST.HARD;
      c += staticCost(t, s, this.config) * this.serviceWeight;
      if (closureHit(t, standId, this.closures)) c += COST.HARD;
      if (this.intlSoon.has(turnId) && s.international === "primary") c += this.config.settings.intlProtectionPenalty;
    }
    const base = this.baseline[turnId]?.standId ?? null;
    if (this.changePenalty > 0 && base !== null && base !== standId) c += this.changePenalty;
    this.unitCache.set(key, c);
    return c;
  }

  pair(a: Turn, b: Turn): number {
    const gap = gapMin(a, b);
    const s = this.config.settings;
    if (gap < s.minBufferMin) return COST.HARD;
    if (gap < s.preferredBufferMin) return (s.preferredBufferMin - gap) * s.tightBufferPenaltyPerMin;
    return 0;
  }

  private standName(id: string): string {
    return this.standById.get(id)?.name ?? id;
  }

  /**
   * Cost of `turnId` on `standId` against everything else currently placed. With `out`,
   * each reason is written down as well, for "Why this stand?".
   */
  interaction(turnId: string, standId: string | null, out?: CostPart[]): number {
    if (standId === null) return 0;
    const t = this.turnById.get(turnId)!;
    let c = 0;
    for (const otherId of this.onStand.get(standId) ?? []) {
      if (otherId === turnId) continue;
      const u = this.turnById.get(otherId)!;
      const p = this.pair(t, u);
      c += p;
      if (out && p > 0) {
        const gap = Math.round(gapMin(t, u));
        out.push(
          p >= COST.HARD
            ? { code: "overlap", cost: p, text: `${named(u)} is on ${this.standName(standId)} with only ${Math.max(0, gap)} min between them` }
            : { code: "tight-buffer", cost: p, text: `Only ${gap} min between this and ${named(u)} on ${this.standName(standId)}` },
        );
      }
    }
    for (const otherStand of this.depStands.get(standId) ?? []) {
      for (const otherId of this.onStand.get(otherStand) ?? []) {
        if (otherId === turnId) continue;
        const u = this.turnById.get(otherId)!;
        if (!overlaps(t.start, t.end, u.start, u.end)) continue;
        if (dependencyApplies(this.config, standId, t, otherStand, u) || dependencyApplies(this.config, otherStand, u, standId, t)) {
          c += COST.HARD;
          out?.push({ code: "dependency", cost: COST.HARD, text: `${named(u)} on ${this.standName(otherStand)} blocks ${this.standName(standId)} at the same time` });
        }
      }
    }
    c += this.customsWalk(t, standId, out);
    c += this.bridgePriority(t, standId, out);
    c += this.together(t, standId, out);
    return c;
  }

  /**
   * Bridges go to jets first. A turboprop on a bridge while a jet that could have used it is
   * on a stand without one, both on the ground at once, costs the same from either side.
   */
  bridgePriority(t: Turn, standId: string, out?: CostPart[]): number {
    const rivals = this.bridgeRivals.get(t.id);
    if (!rivals) return 0;
    const pen = this.config.settings.bridgePriorityPenalty ?? 0;
    const stand = this.standById.get(standId);
    if (!stand) return 0;
    const jet = this.jets.has(t.id);
    let c = 0;
    for (const otherId of rivals) {
      const other = this.standOf.get(otherId);
      if (!other) continue;
      const o = this.standById.get(other);
      if (!o) continue;
      // Turboprop on a bridge; the jet on a stand with none that could have had this bridge.
      const hit = jet
        ? !stand.jetbridge && o.jetbridge && this.candidates.get(t.id)!.includes(other)
        : stand.jetbridge && !o.jetbridge && this.candidates.get(otherId)!.includes(standId);
      if (!hit || c + pen > SOFT_PAIR_CEILING) continue;
      c += pen;
      const u = this.turnById.get(otherId)!;
      out?.push(
        jet
          ? { code: "bridge-priority", cost: pen, text: `${named(u)}, a turboprop, has ${o.name}'s jet bridge while this jet is on ${stand.name} without one` }
          : { code: "bridge-priority", cost: pen, text: `${named(u)}, a jet, is on ${o.name} without a bridge while this turboprop has ${stand.name}'s` },
      );
    }
    return c;
  }

  /**
   * Aircraft kept close: Air Canada's with Air Canada's and WestJet's with WestJet's, gates
   * apart costing a little each; and, far more lightly, each ground handler's aircraft.
   */
  together(t: Turn, standId: string, out?: CostPart[]): number {
    const at = this.order.get(standId);
    if (at === undefined) return 0;
    const apart = (partners: string[] | undefined, pen: number, ceiling: number) => {
      let c = 0;
      let far = 0;
      for (const otherId of partners ?? []) {
        const other = this.standOf.get(otherId);
        const there = other ? this.order.get(other) : undefined;
        if (there === undefined) continue;
        const between = Math.max(0, Math.abs(there - at) - 1);
        if (!between) continue;
        c += pen * between;
        far++;
      }
      // A tie-breaker: never worth more than a few gates, so it cannot outweigh giving another
      // aircraft a stand when that means moving several of the group's.
      return { c: Math.min(c, ceiling), far };
    };
    const airline = apart(this.groupPartners.get(t.id), this.config.settings.handlerSpreadPenalty ?? 0, TOGETHER_CEILING);
    const handler = apart(this.handlerPartners.get(t.id), this.config.settings.handlerTogetherPenalty ?? 3, HANDLER_CEILING);
    if (out && airline.c > 0) {
      const name = this.config.airlines[t.airline]?.name ?? t.airline;
      out.push({ code: "airline-together", cost: airline.c, text: `${airline.far} other ${name} aircraft on the ground at the same time ${airline.far === 1 ? "is" : "are"} gates away from ${this.standName(standId)}` });
    }
    if (out && handler.c > 0) {
      const name = handlerOf(t.airline, this.config)?.name ?? "the same handler";
      out.push({ code: "handler-together", cost: handler.c, text: `${handler.far} other ${name} aircraft on the ground at the same time ${handler.far === 1 ? "is" : "are"} gates away from ${this.standName(standId)}` });
    }
    return airline.c + handler.c;
  }

  /**
   * Passengers meeting on the customs walk: this visit and one whose passengers its own would
   * meet, on two different customs gates. Strong, but below an aircraft with no stand, so a
   * customs gate is still used when nothing else is free. Counted from both sides, like a pair.
   */
  customsWalk(t: Turn, standId: string, out?: CostPart[]): number {
    const partners = this.walkPartners.get(t.id);
    if (!partners || !this.customs.has(standId)) return 0;
    let c = 0;
    const met: string[] = [];
    for (const otherId of partners) {
      const s = this.standOf.get(otherId);
      if (s && s !== standId && this.customs.has(s)) {
        c += this.config.settings.customsWalkPenalty ?? 0;
        met.push(`${named(this.turnById.get(otherId)!)} on ${this.standName(s)}`);
      }
    }
    c = Math.min(c, CUSTOMS_WALK_CEILING);
    if (out && c > 0) out.push({ code: "customs-walk", cost: c, text: `Passengers would meet on the walk to customs: ${met.join(", ")}` });
    return c;
  }


  place(turnId: string, standId: string | null): void {
    const prev = this.standOf.get(turnId) ?? null;
    if (prev !== null) this.onStand.get(prev)!.delete(turnId);
    this.standOf.set(turnId, standId);
    if (standId !== null) this.onStand.get(standId)?.add(turnId);
  }

  /** Change in total cost if `turnId` moved to `standId`. */
  moveDelta(turnId: string, standId: string | null): number {
    const prev = this.standOf.get(turnId) ?? null;
    if (prev === standId) return 0;
    return (
      this.unit(turnId, standId) +
      this.interaction(turnId, standId) -
      this.unit(turnId, prev) -
      this.interaction(turnId, prev)
    );
  }

  totalCost(): number {
    let c = 0;
    for (const t of this.turns) c += this.unit(t.id, this.standOf.get(t.id) ?? null);
    // Each pair/dependency is counted from both sides, so halve.
    let inter = 0;
    for (const t of this.turns) inter += this.interaction(t.id, this.standOf.get(t.id) ?? null);
    return c + inter / 2;
  }

  /** Soft (non-hard, non-stability) cost of the current solution: the "service quality" part. */
  softCost(): number {
    const s = this.config.settings;
    let c = 0;
    for (const t of this.turns) {
      const standId = this.standOf.get(t.id);
      const stand = standId ? this.standById.get(standId) : undefined;
      if (!stand) continue;
      c += staticCost(t, stand, this.config);
      if (this.intlSoon.has(t.id) && stand.international === "primary") c += s.intlProtectionPenalty;
      for (const otherId of this.onStand.get(stand.id) ?? []) {
        const u = this.turnById.get(otherId)!;
        if (u.id <= t.id) continue;
        const gap = gapMin(t, u);
        if (gap >= s.minBufferMin && gap < s.preferredBufferMin) c += (s.preferredBufferMin - gap) * s.tightBufferPenaltyPerMin;
      }
      // Each pair once: both sides count it, so halve.
      c += this.customsWalk(t, stand.id) / 2;
      c += this.bridgePriority(t, stand.id) / 2;
      c += this.together(t, stand.id) / 2;
    }
    return c;
  }

  snapshot(): Map<string, string | null> {
    return new Map(this.standOf);
  }

  restore(snap: Map<string, string | null>): void {
    for (const [turnId, standId] of snap) this.place(turnId, standId);
  }
}

export function allocateStands(opts: AllocateOptions): AllocateResult {
  const started = Date.now();
  const {
    turns,
    config,
    closures = [],
    current = {},
    changePenalty = 0,
    serviceWeight = 1,
    now,
    seed = 42,
    // Doubled when pushback spacing was added: pairs of departures make the cost landscape
    // rougher, and an aircraft parked all day, which needs a whole stand cleared, was left
    // off at 40,000. A YLW day still runs in about a fifth of the time budget.
    iterations = 80_000,
    // A ceiling for a slow or busy machine, not a target: a YLW day runs its passes in about
    // 200 ms. At 600 ms a loaded machine stopped short often enough that an aircraft parked
    // all day, which needs a whole stand cleared, was left off one run in a few.
    timeBudgetMs = 1500,
  } = opts;
  const rand = mulberry32(seed);
  const model = new StandModel(turns, config, closures, current, changePenalty, serviceWeight);

  // Fixed turns: user-locked, or aircraft already on block before `now`.
  for (const t of turns) {
    const cur = current[t.id];
    const onBlock = now !== undefined && t.arr?.actual != null && t.start <= now;
    const departed = now !== undefined && t.dep?.actual != null && t.end <= now;
    if (cur?.standId && (cur.locked || onBlock || departed)) {
      model.fixed.add(t.id);
      model.place(t.id, cur.standId);
    }
  }

  // Construction: most constrained first, then earliest.
  const free = turns.filter((t) => !model.fixed.has(t.id));
  free.sort(
    (a, b) =>
      model.candidates.get(a.id)!.length - model.candidates.get(b.id)!.length ||
      a.start - b.start ||
      a.id.localeCompare(b.id),
  );
  for (const t of free) {
    let best: string | null = null;
    let bestDelta = model.moveDelta(t.id, null);
    for (const s of model.candidates.get(t.id)!) {
      const d = model.moveDelta(t.id, s);
      if (d < bestDelta) {
        bestDelta = d;
        best = s;
      }
    }
    model.place(t.id, best);
  }

  // Improvement: simulated annealing over single moves and swaps.
  let cost = model.totalCost();
  let bestCost = cost;
  let bestSnap = model.snapshot();
  const movable = free.map((t) => t.id);
  let it = 0;
  const unallocated = () => movable.filter((id) => (model.standOf.get(id) ?? null) === null);
  /** One pass of `iterations` moves, hot to cold. False when the time budget cut it short. */
  const anneal = (): boolean => {
    const T0 = 300;
    const T1 = 0.5;
    for (let k = 0; k < iterations; k++, it++) {
      if ((k & 1023) === 0 && Date.now() - started > timeBudgetMs) return false;
      const temp = T0 * Math.pow(T1 / T0, k / iterations);
      const a = movable[Math.floor(rand() * movable.length)]!;
      const cands = model.candidates.get(a)!;
      const from = model.standOf.get(a) ?? null;
      if (rand() < 0.6 || cands.length === 0) {
        // Single move (including to/from unallocated).
        const to = cands.length === 0 || rand() < 0.03 ? null : cands[Math.floor(rand() * cands.length)]!;
        if (to === from) continue;
        const delta = model.moveDelta(a, to);
        if (delta <= 0 || rand() < Math.exp(-delta / temp)) {
          model.place(a, to);
          cost += delta;
        }
      } else {
        // Swap with a turn on another stand.
        const b = movable[Math.floor(rand() * movable.length)]!;
        const sb = model.standOf.get(b) ?? null;
        if (a === b || sb === from) continue;
        if (sb !== null && !cands.includes(sb)) continue;
        if (from !== null && !model.candidates.get(b)!.includes(from)) continue;
        const d1 = model.moveDelta(a, sb);
        model.place(a, sb);
        const d2 = model.moveDelta(b, from);
        const delta = d1 + d2;
        if (delta <= 0 || rand() < Math.exp(-delta / temp)) {
          model.place(b, from);
          cost += delta;
        } else {
          model.place(a, from);
        }
      }
      if (cost < bestCost - 1e-9) {
        bestCost = cost;
        bestSnap = model.snapshot();
      }
    }
    return true;
  };
  if (movable.length > 0) {
    let finished = anneal();
    /**
     * A pass that ran out of moves rather than time can leave an aircraft with no stand
     * while a stand it may use sits free: the pass freed the stand late and never drew
     * that pair again. On a dense day that is the rule, not the exception. So while
     * something is still off a stand and there is time, another pass from the best plan
     * so far, until a pass finds nothing better.
     */
    for (let pass = 0; finished && pass < EXTRA_PASSES && unallocated().length > 0; pass++) {
      model.restore(bestSnap);
      cost = bestCost;
      const before = bestCost;
      finished = anneal();
      if (bestCost >= before - 1e-9) break;
    }
  }
  model.restore(bestSnap);

  // Whatever is still off a stand goes onto the cheapest stand that breaks no hard rule -
  // size, airline, type, customs, closure, dependency, the minimum gap - if there is one.
  // Every soft cost together is a fraction of what an aircraft on no stand already costs.
  for (const id of unallocated()) {
    let best: string | null = null;
    let bestDelta = Infinity;
    for (const s of model.candidates.get(id)!) {
      if (model.unit(id, s) + model.interaction(id, s) >= COST.HARD) continue;
      const d = model.moveDelta(id, s);
      if (d < bestDelta) {
        bestDelta = d;
        best = s;
      }
    }
    if (best !== null) model.place(id, best);
  }

  repairBridges(model);
  repairHeavy(model);
  polish(model);

  const assignments: Record<string, StandAssignment> = {};
  for (const t of turns) {
    assignments[t.id] = {
      turnId: t.id,
      standId: model.standOf.get(t.id) ?? null,
      locked: current[t.id]?.locked ?? false,
    };
  }
  return { assignments, cost: model.totalCost(), iterations: it, elapsedMs: Date.now() - started };
}

/**
 * After the search: an aircraft breaking a hard rule (a clash, the customs walk, a closed
 * stand) or left with no stand gets one more look, a stand at a time: whoever is in its way
 * there moves to their own best other stand, and the trade is kept when the plan as a whole
 * is better. One move at a time cannot find these - clearing the stand looks worse on its
 * own - so a recovery left a departure boarding across the customs walk, and an aircraft
 * parked all day, which needs a whole stand cleared, could be left with none.
 */
function repairHeavy(model: StandModel): void {
  const heavy = (id: string) => {
    const at = model.standOf.get(id) ?? null;
    if (at === null) return true;
    // The customs walk (3000) and anything harder; no soft rule alone comes near.
    return model.unit(id, at) + model.interaction(id, at) >= 1000;
  };
  for (let round = 0; round < 2; round++) {
    let improved = false;
    for (const t of model.turns) {
      if (model.fixed.has(t.id) || !heavy(t.id)) continue;
      for (const target of model.candidates.get(t.id)!) {
        if (target === model.standOf.get(t.id)) continue;
        const inWay = [...(model.onStand.get(target) ?? [])].map((id) => model.turnById.get(id)!).filter((u) => overlaps(t.start, t.end, u.start, u.end));
        if (inWay.some((u) => model.fixed.has(u.id))) continue;
        // The change in total cost, step by step, rather than pricing the whole plan twice.
        let delta = 0;
        const snap = model.snapshot();
        for (const u of inWay) {
          delta += model.moveDelta(u.id, null);
          model.place(u.id, null);
        }
        delta += model.moveDelta(t.id, target);
        model.place(t.id, target);
        for (const u of inWay) {
          let best: string | null = null;
          let bestDelta = 0;
          for (const c of model.candidates.get(u.id)!) {
            const d = model.moveDelta(u.id, c);
            if (d < bestDelta) {
              bestDelta = d;
              best = c;
            }
          }
          delta += bestDelta;
          model.place(u.id, best);
        }
        if (delta < -1e-6) {
          improved = true;
          break;
        }
        model.restore(snap);
      }
    }
    if (!improved) break;
  }
}

/**
 * After the search: every aircraft that may move gets every stand tried, and any move that
 * makes the plan as a whole cheaper is taken, until none does. The random search ends where
 * its last pass left it, and that can be one move short: WS3719 left boarding on Stand 3
 * across the customs walk with Stand 2 free, or a jet on a stand with no bridge beside a
 * free one. A few thousand prices for a YLW day; it never makes the plan worse.
 */
function polish(model: StandModel): void {
  for (let round = 0; round < 4; round++) {
    let improved = false;
    for (const t of model.turns) {
      if (model.fixed.has(t.id)) continue;
      let best: string | null | undefined;
      let bestDelta = -1e-6;
      for (const s of model.candidates.get(t.id)!) {
        if (s === model.standOf.get(t.id)) continue;
        const d = model.moveDelta(t.id, s);
        if (d < bestDelta) {
          bestDelta = d;
          best = s;
        }
      }
      if (best !== undefined) {
        model.place(t.id, best);
        improved = true;
      }
    }
    if (!improved) break;
  }
}

/**
 * After the search: any jet left on a stand without a bridge gets one more look. Where a
 * turboprop holds a bridge the jet could use, try putting the jet there and the turboprop
 * on the best stand left for it - the jet's own, or any other - and keep it if the plan as
 * a whole is better. The search moves one aircraft at a time and seldom finds this trade
 * on its own: each half of it, alone, looks worse.
 */
function repairBridges(model: StandModel): void {
  const movable = (id: string) => !model.fixed.has(id);
  for (let round = 0; round < 3; round++) {
    let improved = false;
    for (const jet of model.turns) {
      const at = model.standOf.get(jet.id);
      if (!at || !movable(jet.id) || model.standById.get(at)?.jetbridge) continue;
      if (!wantsBridge(jet, model.config)) continue;
      for (const bridge of model.candidates.get(jet.id)!) {
        if (!model.standById.get(bridge)?.jetbridge) continue;
        const holders = [...(model.onStand.get(bridge) ?? [])]
          .map((id) => model.turnById.get(id)!)
          .filter((u) => overlaps(jet.start, jet.end, u.start, u.end));
        if (!holders.every((u) => movable(u.id) && !isJet(u.aircraftType, model.config))) continue;
        const before = model.totalCost();
        const snap = model.snapshot();
        for (const u of holders) model.place(u.id, null);
        model.place(jet.id, bridge);
        for (const u of holders) {
          let best: string | null = null;
          let bestDelta = model.moveDelta(u.id, null);
          for (const s of model.candidates.get(u.id)!) {
            const d = model.moveDelta(u.id, s);
            if (d < bestDelta) {
              bestDelta = d;
              best = s;
            }
          }
          model.place(u.id, best);
        }
        if (model.totalCost() < before - 1e-9) {
          improved = true;
          break;
        }
        model.restore(snap);
      }
    }
    if (!improved) break;
  }
}

/** Price an existing assignment set with the same model the optimizer uses. */
export function planCost(
  turns: Turn[],
  config: AirportConfig,
  assignments: Record<string, StandAssignment>,
  closures: StandClosure[] = [],
): number {
  const model = new StandModel(turns, config, closures, {}, 0, 1);
  for (const t of turns) model.place(t.id, assignments[t.id]?.standId ?? null);
  return model.totalCost();
}
